/**
 * Router de pos-web. Rutas pesadas (Stats con AG Charts ~200KB,
 * Inventory, Tables con @dnd-kit, Settings) cargan con `React.lazy` para
 * no arrastrar sus deps al bundle inicial. Home, Login y los flujos del
 * core (productos, pedidos, caja) cargan eager — son lo que se abre en
 * el 90% de las sesiones.
 */

import {
  createBrowserHistory,
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';
import { lazy, Suspense, type ComponentType } from 'react';

import { AppShell } from './components/AppShell.js';
import { RowsSkeleton } from './components/skeletons.js';
import { CashPage } from './pages/Cash.js';
import { CashReportPage } from './pages/CashReport.js';
import { CategoriesPage } from './pages/Categories.js';
import { HomePage } from './pages/Home.js';
import { OrderEditPage } from './pages/OrderEdit.js';
import { OrderNewPage } from './pages/OrderNew.js';
import { OrderReceiptPage } from './pages/OrderReceipt.js';
import { OrdersPage } from './pages/Orders.js';
import { ProductEditPage } from './pages/ProductEdit.js';
import { ProductsPage } from './pages/Products.js';

// --- Rutas pesadas: lazy-loaded ---
// `React.lazy` espera `{ default }` — mapeamos desde la export nombrada
// al default vía un helper genérico para no repetir el boilerplate.
const lazyNamed = <T,>(
  importer: () => Promise<Record<string, unknown>>,
  exportName: string,
): ComponentType<T> =>
  lazy(() =>
    importer().then((m) => ({
      default: m[exportName] as ComponentType<T>,
    })),
  );

const StatsPage = lazyNamed<Record<string, never>>(
  () => import('./pages/Stats.js'),
  'StatsPage',
);
const InventoryPage = lazyNamed<Record<string, never>>(
  () => import('./pages/Inventory.js'),
  'InventoryPage',
);
const TablesPage = lazyNamed<Record<string, never>>(
  () => import('./pages/Tables.js'),
  'TablesPage',
);
const SettingsPage = lazyNamed<Record<string, never>>(
  () => import('./pages/Settings.js'),
  'SettingsPage',
);
const ExpensesPage = lazyNamed<Record<string, never>>(
  () => import('./pages/Expenses.js'),
  'ExpensesPage',
);
const CustomersPage = lazyNamed<Record<string, never>>(
  () => import('./pages/Customers.js'),
  'CustomersPage',
);

// Fallback global mientras el bundle de la página cargada con `React.lazy`
// llega del network. Usamos un skeleton genérico (filas) para que la
// transición lazy → render-real no muestre un texto fugaz "Cargando…".
const LazyFallback = () => (
  <div className="p-8">
    <RowsSkeleton count={8} />
  </div>
);

const withSuspense = (Component: ComponentType) =>
  function WithSuspense() {
    return (
      <Suspense fallback={<LazyFallback />}>
        <Component />
      </Suspense>
    );
  };

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
});

const categoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/categories',
  component: CategoriesPage,
});

const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products',
  component: ProductsPage,
  // `?categoryId=<id>` hace que el filtro sobreviva reloads y sea
  // shareable. Validamos manual para no arrastrar zod al router.
  validateSearch: (search: Record<string, unknown>): { categoryId?: string } => {
    const cat = search['categoryId'];
    return typeof cat === 'string' && cat.length > 0 ? { categoryId: cat } : {};
  },
});

const productNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/new',
  component: function ProductNew() {
    return <ProductEditPage mode="create" />;
  },
});

const productEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productId',
  component: function ProductEdit() {
    const { productId } = productEditRoute.useParams();
    return <ProductEditPage mode="edit" productId={productId} />;
  },
});

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders',
  component: OrdersPage,
});

const orderNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders/new',
  component: OrderNewPage,
});

const orderEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders/$orderId',
  component: function OrderEdit() {
    const { orderId } = orderEditRoute.useParams();
    return <OrderEditPage orderId={orderId} />;
  },
});

const cashRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cash',
  component: CashPage,
});

const cashReportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/cash/$sessionId',
  component: function CashReport() {
    const { sessionId } = cashReportRoute.useParams();
    return <CashReportPage sessionId={sessionId} />;
  },
});

const orderReceiptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/order/$orderId',
  component: function OrderReceipt() {
    const { orderId } = orderReceiptRoute.useParams();
    return <OrderReceiptPage orderId={orderId} />;
  },
});

const statsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stats',
  component: withSuspense(StatsPage),
});

const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory',
  component: withSuspense(InventoryPage),
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: withSuspense(SettingsPage),
});

const tablesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tables',
  component: withSuspense(TablesPage),
});

const expensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/expenses',
  component: withSuspense(ExpensesPage),
});

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  component: withSuspense(CustomersPage),
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  categoriesRoute,
  productsRoute,
  productNewRoute,
  productEditRoute,
  ordersRoute,
  orderNewRoute,
  orderEditRoute,
  cashRoute,
  cashReportRoute,
  orderReceiptRoute,
  statsRoute,
  inventoryRoute,
  settingsRoute,
  tablesRoute,
  expensesRoute,
  customersRoute,
]);

export const router = createRouter({
  routeTree,
  history: createBrowserHistory(),
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
